/**
 * Shared hedge/knowledge-gap detector for conversation transcripts.
 *
 * Extracted verbatim (behaviour-preserving) from
 * `src/components/conversation/details/ConversationKnowledgeGaps.tsx` so the
 * single-conversation card and the Knowledge Base's cross-conversation gap
 * panel run the exact same detector instead of two drifting copies.
 *
 * Pure heuristic, no model call. It scans for hedge phrases ("I don't know",
 * "let me check", "I'm not sure", …) immediately following a caller question.
 * A richer detector can replace the internals here without touching callers.
 */

export interface TranscriptGap {
  /** Turn index within the transcript. */
  index: number;
  question: string;
  agentReply: string;
  timestamp: string | null;
}

/**
 * Any caller question, whether or not the genie could answer it. This is what
 * "most asked questions" is built from — a gap scan alone only ever sees the
 * questions the genie fumbled, which is the opposite of what a support page
 * should lead with.
 */
export interface TranscriptQuestion {
  /** Turn index within the transcript. */
  index: number;
  question: string;
  /** True when the reply that followed hedged — the genie couldn't answer. */
  hedged: boolean;
  timestamp: string | null;
}

interface NormalisedTurn {
  index: number;
  speaker: "agent" | "user";
  text: string;
  timestamp: string | null;
}

const HEDGE_PHRASES = [
  "i don't know",
  "i don't have",
  "i'm not sure",
  "i am not sure",
  "i cannot find",
  "i can't find",
  "i can't help",
  "i'm not able",
  "i am not able",
  "i don't have access",
  "let me check",
  "i'll have to check",
  "you'll need to",
  "you would need to",
  "i'm afraid",
  "unfortunately, i",
  "i don't currently",
  "that's outside",
  "outside my scope",
  "outside my knowledge",
  "i wasn't trained",
  "not something i can",
];

const QUESTION_KEYWORDS = [
  "how ",
  "what ",
  "when ",
  "where ",
  "why ",
  "who ",
  "which ",
  "can you",
  "could you",
  "do you",
  "does it",
  "is there",
  "are there",
  "will you",
  "would you",
];

export function isQuestion(text: string): boolean {
  if (text.includes("?")) return true;
  const lower = text.toLowerCase().trim();
  return QUESTION_KEYWORDS.some((kw) => lower.startsWith(kw) || lower.includes(` ${kw}`));
}

export function hasHedge(text: string): boolean {
  const lower = text.toLowerCase();
  return HEDGE_PHRASES.some((phrase) => lower.includes(phrase));
}

function formatSeconds(s: number): string {
  if (!isFinite(s)) return "";
  const total = Math.floor(s);
  const m = Math.floor(total / 60);
  const sec = total % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

function normaliseTurn(raw: unknown, index: number): NormalisedTurn | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const text =
    typeof r.text === "string"
      ? r.text
      : typeof r.message === "string"
        ? r.message
        : typeof r.content === "string"
          ? r.content
          : "";
  if (!text || text.length < 4) return null;

  const speakerRaw =
    typeof r.speaker === "string" ? r.speaker : typeof r.role === "string" ? r.role : "user";
  const speaker: "agent" | "user" =
    speakerRaw === "agent" || speakerRaw === "assistant" ? "agent" : "user";

  let timestamp: string | null = null;
  if (typeof r.timestamp === "string") timestamp = r.timestamp;
  else if (typeof r.time === "string") timestamp = r.time;
  else if (typeof r.timestamp === "number") timestamp = formatSeconds(r.timestamp);

  return { index, speaker, text, timestamp };
}

/**
 * Detects caller questions the genie hedged on.
 *
 * @param transcript raw transcript array off `conversation.details.transcript`
 * @param limit max gaps returned (default 3, matching the conversation card)
 */
export function detectTranscriptGaps(transcript: unknown, limit = 3): TranscriptGap[] {
  if (!Array.isArray(transcript) || transcript.length === 0) return [];

  const normalised = transcript
    .map((raw, index) => normaliseTurn(raw, index))
    .filter((t): t is NormalisedTurn => t !== null);

  const out: TranscriptGap[] = [];

  for (let i = 0; i < normalised.length; i++) {
    const turn = normalised[i];
    if (turn.speaker !== "user") continue;
    if (!isQuestion(turn.text)) continue;

    const reply = normalised.slice(i + 1, i + 3).find((t) => t.speaker === "agent");
    if (!reply) continue;
    if (!hasHedge(reply.text)) continue;

    out.push({
      index: turn.index,
      question: turn.text.trim(),
      agentReply: reply.text.trim(),
      timestamp: turn.timestamp,
    });
  }

  return out.slice(0, limit);
}

/**
 * Detects every caller question in a transcript, marking the ones the genie
 * hedged on. Same turn normalisation and question test as the gap detector, so
 * a question counted here and a gap found there can never disagree about what
 * was asked.
 *
 * @param transcript raw transcript array off `conversation.details.transcript`
 * @param limit max questions returned per conversation (default 8)
 */
export function detectTranscriptQuestions(
  transcript: unknown,
  limit = 8,
): TranscriptQuestion[] {
  if (!Array.isArray(transcript) || transcript.length === 0) return [];

  const normalised = transcript
    .map((raw, index) => normaliseTurn(raw, index))
    .filter((t): t is NormalisedTurn => t !== null);

  const out: TranscriptQuestion[] = [];

  for (let i = 0; i < normalised.length; i++) {
    const turn = normalised[i];
    if (turn.speaker !== "user") continue;
    if (!isQuestion(turn.text)) continue;

    const reply = normalised.slice(i + 1, i + 3).find((t) => t.speaker === "agent");

    out.push({
      index: turn.index,
      question: turn.text.trim(),
      hedged: Boolean(reply && hasHedge(reply.text)),
      timestamp: turn.timestamp,
    });
  }

  return out.slice(0, limit);
}

/** Loose key used to collapse near-identical questions across conversations. */
export function questionKey(question: string): string {
  return question
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}
